import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { DefaultStyles, User, UserPreferences } from '@23degrees/shared-code/models';

import { map } from 'rxjs/operators';
import { EnvironmentService } from '../env/env.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);

  private envService: EnvironmentService = inject(EnvironmentService);

  baseUrl = '/';

  loginUrl = `${this.baseUrl}services/auth/login`;
  oauthLoginUrl = `${this.baseUrl}services/auth/login/oauth`;
  connectorLoginUrl = `${this.baseUrl}services/auth/login/connector`;
  verifyUrl = `${this.baseUrl}services/auth/verify`;
  deleteUnverifiedUrl = `${this.baseUrl}/services/public/user/delete-unverified`;
  signupUrl = `${this.baseUrl}services/auth/register`;
  confirmUrl = `${this.baseUrl}services/auth/confirm`;
  resetPasswordUrl = `${this.baseUrl}services/auth/reset-password`;
  newPasswordUrl = `${this.baseUrl}services/auth/new-password`;
  changePasswordUrl = `${this.baseUrl}services/auth/profile/changepass`;
  updateUserInfoUrl = `${this.baseUrl}services/pub/api/v1/user/update/userinfo`;
  userUrl = `${this.baseUrl}services/auth/profile`;
  userDataUrl = `${this.baseUrl}services/pub/api/v1/user/id`;
  userDataSlugUrl = `${this.envService.APIV1}/user/slug`;
  consentUrl = `${this.baseUrl}services/auth/consent`;
  resendConfirmationUrl = `${this.baseUrl}services/auth/resend`;
  updateStyleUrl = `${this.baseUrl}services/pub/api/v1/user/update/defaultstyles`;
  updatePreferencesUrl = `${this.baseUrl}services/pub/api/v1/user/update/userpreferences`;
  updatePreferencesAndStyleUrl = `${this.baseUrl}services/pub/api/v1/user/update/defaultstylesandpreferences`;
  uploadUserLogo = `${this.baseUrl}services/pub/api/v1/user/update/logo`;

  // New FileService
  defaultContentLogo = `${this.baseUrl}api/v2/files/default-content-logo`;
  profilePicture = `${this.baseUrl}api/v2/files/profile-picture`;

  userInfo = `${this.baseUrl}services/pub/api/v1/user/info`;
  andonUserInfo = `${this.baseUrl}services/public/anonuser`;
  newsletterConsentUrl = `${this.baseUrl}services/pub/api/v1/user/update/newsletterconsent`;

  headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  options = { headers: this.headers };
  userLoggedIn: boolean = false;

  changePassword(options) {
    return this.http.put(this.changePasswordUrl, options).toPromise();
  }

  verifyToken(token: string, id: string) {
    return this.http.post(`${this.verifyUrl}`, { token: token, id: id }, { observe: 'response' }).pipe(
      map((res: HttpResponse<any>) => {
        if (res.status === 200) {
          return true;
        }
        return false;
      }),
    );
  }

  // login(userLogin: UserLogin) {
  //     return this.http
  //       .post<{data: User}>(this.loginUrl, userLogin, this.options)
  //       .pipe(
  //         map(res => res.data)
  //       );
  // }

  login(userLogin) {
    return this.http.post<{ data: any }>(this.loginUrl, userLogin, this.options).pipe(
      map((res) => {
        //console.log('[AuthService][Login]', { res });
        return res;
      }),
      map((res) => res.data),
    );
  }

  oauthLogin(credentials) {
    return this.http.post<{ data: any }>(this.oauthLoginUrl, credentials, this.options).pipe(
      map((res) => {
        //console.log('[AuthService][Login]', { res });
        return res;
      }),
      map((res) => res.data),
    );
  }

  connectorLogin(credentials) {
    return this.http.post<{ data: any }>(this.connectorLoginUrl, credentials, this.options).pipe(
      map((res) => {
        //console.log('[AuthService][Login]', { res });
        return res;
      }),
      map((res) => res.data),
    );
  }

  getAnonUserInfo(): Observable<any> {
    return this.http.get<any>(this.andonUserInfo, this.options);
  }

  getUserInfo(): Observable<any> {
    return this.http.get<any>(this.userInfo, this.options);
  }

  getUserMeta(id): Observable<any> {
    return this.http.get(`${this.userUrl}/${id}`, this.options).pipe(map((user) => console.log('USER ->', user)));
  }

  getUserById(id): Observable<any> {
    return this.http.get<any>(`${this.userDataUrl}/${id}`, this.options).pipe(
      map((data) => {
        return data;
      }),
    );
  }

  getUserByIdPromise(id): Promise<any> {
    return this.http.get<any>(`${this.userDataUrl}/${id}`, this.options).toPromise();
  }

  getUserBySlug(slug): Observable<User> {
    return this.http.get<User>(`${this.userDataSlugUrl}/${slug}`, this.options);
  }

  getUserBySlugPromise(slug): Promise<User> {
    return this.http.get<User>(`${this.userDataSlugUrl}/${slug}`, this.options).toPromise();
  }

  signup(signup, language, cookieConsent = null): Observable<any> {
    console.log('Signup language', language);
    const { email, password, name, newsletter } = signup;
    const newsletterConsent = { accepted: newsletter, date: null };
    return this.http
      .post(this.signupUrl, { email, password, name, lang: language, cookieConsent, newsletterConsent }, this.options)
      .pipe(map((res: any) => res.data));
  }

  confirmSubscription(token) {
    return this.http.post(this.confirmUrl, { token }, this.options);
  }

  resetPassword(email) {
    return this.http.post(this.resetPasswordUrl, { email: email }, this.options);
  }

  setNewPassword(password, token) {
    return this.http.post(this.newPasswordUrl, { password, token }, this.options);
  }

  resendConfirmationToken(email: string) {
    console.log('Resending!');
    return this.http.post(this.resendConfirmationUrl, { email }, this.options);
  }

  acceptConditions() {
    console.log('Accepting conditions!');
    return this.http.get(this.consentUrl, this.options);
  }

  updateUserInfo(name: string, email: string) {
    if (!name || !email) {
      throw new Error('Name and email are required');
    }
    return this.http.patch(this.updateUserInfoUrl, { name, email }, this.options);
  }

  updateStyle(style: DefaultStyles) {
    return this.http.post<any>(this.updateStyleUrl, style, this.options).pipe(map((res) => res));
  }

  updatePreferencesPromise(userPreferences: any) {
    return this.http.post<any>(this.updatePreferencesUrl, userPreferences, this.options).toPromise();
  }

  updatePreferencesAndStyle(userPreferences: any) {
    return this.http.post<any>(this.updatePreferencesAndStyleUrl, userPreferences, this.options);
  }

  updatePreferencesAndStylePromise(userPreferences: any) {
    return this.http.post<any>(this.updatePreferencesAndStyleUrl, userPreferences, this.options).toPromise();
  }

  updatePreferences(preferences: UserPreferences) {
    // console.debug('Updating preferences with', preferences);
    return this.http.post<any>(this.updatePreferencesUrl, preferences, this.options).pipe(map((res) => res.data));
  }

  deleteLogo() {
    return this.http.delete(this.uploadUserLogo, this.options);
  }

  deleteProfilePicture() {
    return this.http.delete(this.profilePicture, this.options);
  }

  deleteDefaultContentLogo() {
    return this.http.delete(this.defaultContentLogo, this.options);
  }

  deleteLogoPromise() {
    return this.http.delete(this.uploadUserLogo, this.options).toPromise();
  }

  deleteUser() {
    return this.http.delete('/services/pub/api/v1/user/delete/user').toPromise();
  }

  deleteUnverifiedSubscription(token: string) {
    return this.http.post(`${this.deleteUnverifiedUrl}`, { token: token }, { observe: 'response' }).pipe(
      map((res: HttpResponse<any>) => {
        if (res.status === 200) {
          return true;
        }
        return false;
      }),
    );
  }

  updateNewsletterConsent(newsletterConsent) {
    return this.http.post<any>(this.newsletterConsentUrl, newsletterConsent, this.options);
  }
  // !DELETE PLZ TEMP
  updateNewsletterConsentPromise(newsletterConsent) {
    return this.http.post(this.newsletterConsentUrl, newsletterConsent, this.options).toPromise();
  }
}
